import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import type { IItem } from '@/interfaces/IItem';
import type { IOptionItem } from '@/interfaces/IOptionItem';
import { roundPrice } from '@/utils';

export interface IGpthelpTaxCtx {
  taxPercent: number | null;
  taxMode: TaxDisplayMode | null;
}

export interface IGpthelpItemPrices {
  base: number; // raw item.price (0 when not finite)
  discounted: number | null; // raw discountedPrice, only when finite AND !== base
  final: number; // roundPrice(applyTax(discounted ?? base))
  finalBeforeDiscount: number | null; // roundPrice(applyTax(base)) only when discounted !== null
}

// Verbatim extraction of the applyTaxPartial behavior previously inlined in the
// V1 host-context builder — the context and ALL tools price identically.
function applyTax(price: number, taxCtx: IGpthelpTaxCtx): number {
  const { taxPercent, taxMode } = taxCtx;

  if (!taxPercent || !Number.isFinite(taxPercent)) return price;
  if (typeof taxMode !== 'number' || taxMode === TaxDisplayMode.NoTax)
    return price;

  if (
    taxMode === TaxDisplayMode.AddAndShow ||
    taxMode === TaxDisplayMode.SilentAdd
  ) {
    return price * (1 + taxPercent / 100);
  }

  return price;
}

export function computeItemPrices(
  item: IItem,
  taxCtx: IGpthelpTaxCtx
): IGpthelpItemPrices {
  const base =
    typeof item.price === 'number' && Number.isFinite(item.price)
      ? item.price
      : 0;

  const discounted =
    typeof item.discountedPrice === 'number' &&
    Number.isFinite(item.discountedPrice) &&
    item.discountedPrice !== base
      ? item.discountedPrice
      : null;

  return {
    base,
    discounted,
    final: roundPrice(applyTax(discounted ?? base, taxCtx)),
    finalBeforeDiscount:
      discounted !== null ? roundPrice(applyTax(base, taxCtx)) : null,
  };
}

/** Price before tax of a price-variant choice (same pick as the POS item-price). */
export function optionItemPriceBeforeTax(optionItem: IOptionItem): number {
  const price = Number(optionItem.discountedCost ?? optionItem.cost);
  return Number.isFinite(price) ? price : 0;
}
